/**
 * Structure and component damage.
 *
 * Two honest caveats, both from flight-tunables.md §5. First, the *only*
 * structural numbers in `<PLANE>.DAT` are one wing-strength scalar (offset 104)
 * and two radii (target size 140, collision size 172) — the component model
 * below is a port-side invention and is labelled as one. Second, `0` in the
 * wing-strength field is a **sentinel meaning the wings never fail**, not "the
 * weakest airframe": the S.E.5a, SPAD 13, D.VII, Bristol, Gotha and Handley
 * Page all carry it, and every one of them is described in the game's own
 * flavour text as dive-proof.
 *
 * Overstress itself is Expert + AIRCRAFT MAY BE DAMAGED, per the manual: "if
 * too much stress is put upon your aircraft's wings during a high speed dive,
 * your wings may break!"
 */

import { bodyAlphaRad } from "./aero.ts";
import type { RngBank } from "./rng.ts";
import type { SubsystemFlags } from "./tiers.ts";
import type { Tunables } from "./tunables.ts";
import { clamp } from "./units.ts";

export type ComponentId = "engine" | "wingLeft" | "wingRight" | "controls" | "fuel";

export interface StructureSpec {
  /** Raw `.DAT` offset 104, or `null` for the `0` sentinel — wings that never fail. */
  readonly wingStrengthRaw: number | null;
  /** Positive limit load factor derived from the strength, or `null` when indestructible. */
  readonly limitLoadFactor: number | null;
  /** Raw `.DAT` offset 140 — 120 fleet-wide, 300 for the Gotha and Handley Page. */
  readonly targetSizeRaw: number;
  /** Raw `.DAT` offset 172 — 100 fighters / 180 two-seaters / 300 heavies. */
  readonly collisionSizeRaw: number;
}

export interface DamageState {
  readonly engine: number;
  readonly wingLeft: number;
  readonly wingRight: number;
  readonly controls: number;
  readonly fuel: number;
  readonly wingsFailed: boolean;
  /** Highest load factor the airframe has seen this flight, for debriefs and tests. */
  readonly peakLoadFactor: number;
}

export interface ComponentHit {
  readonly component: ComponentId;
  /** Health removed, 0..1. */
  readonly amount: number;
}

export interface StructureUpdate {
  readonly structure: StructureSpec;
  readonly flags: SubsystemFlags;
  readonly tunables: Tunables;
  readonly loadFactor: number;
  readonly airspeedMps: number;
  readonly vmaxMps: number;
  readonly rng: RngBank;
  readonly dt: number;
}

export const UNDAMAGED: DamageState = {
  engine: 1,
  wingLeft: 1,
  wingRight: 1,
  controls: 1,
  fuel: 1,
  wingsFailed: false,
  peakLoadFactor: 0,
};

/** Converts the raw wing-strength field to a limit load factor; `0`/`null` stays indestructible. */
export function limitLoadFactorFrom(wingStrengthRaw: number | null, tunables: Tunables): number | null {
  if (wingStrengthRaw === null || wingStrengthRaw === 0) return null;
  return wingStrengthRaw / tunables.wingStrengthPerG;
}

/**
 * Effective structural stress: load factor, amplified past the aircraft's own
 * top speed because the manual's failure mode is specifically a *high-speed*
 * dive rather than a hard pull at any speed.
 */
export function structuralStress(update: StructureUpdate): number {
  const overspeed = Math.max(0, (update.airspeedMps - update.vmaxMps) / Math.max(update.vmaxMps, 1));
  return Math.abs(update.loadFactor) * (1 + overspeed * update.tunables.diveOverspeedStressGain);
}

export interface StructureResult {
  readonly damage: DamageState;
  readonly wingFailedNow: boolean;
}

export function updateStructure(state: DamageState, update: StructureUpdate): StructureResult {
  const { tunables } = update;
  const peakLoadFactor = Math.max(state.peakLoadFactor, Math.abs(update.loadFactor));
  const limit = update.structure.limitLoadFactor;

  if (!update.flags.overstressFailure || limit === null || state.wingsFailed) {
    return { damage: { ...state, peakLoadFactor }, wingFailedNow: false };
  }

  const effectiveLimit = update.loadFactor < 0 ? limit * tunables.negativeGLimitFactor : limit;
  const exceedance = structuralStress(update) / Math.max(effectiveLimit, 1e-3);
  if (exceedance <= 1) return { damage: { ...state, peakLoadFactor }, wingFailedNow: false };

  const hazard = tunables.overstressFailureRatePerSecond * (exceedance - 1) * (exceedance - 1) * update.dt;
  if (!update.rng.chance("damage", hazard)) {
    return { damage: { ...state, peakLoadFactor }, wingFailedNow: false };
  }

  const rightWing = update.rng.nextFloat("damage") < 0.5;
  return {
    damage: {
      ...state,
      peakLoadFactor,
      wingsFailed: true,
      wingLeft: rightWing ? state.wingLeft : 0,
      wingRight: rightWing ? 0 : state.wingRight,
    },
    wingFailedNow: true,
  };
}

/**
 * Hook for the weapons/flak lane: applies component hits to the airframe. The
 * distribution of hits by aspect, and the pilot wound path (physiology.ts),
 * belong to that lane — this only owns what a hit does once it has landed.
 */
export function applyComponentDamage(state: DamageState, hits: readonly ComponentHit[]): DamageState {
  const health: Record<ComponentId, number> = {
    engine: state.engine,
    wingLeft: state.wingLeft,
    wingRight: state.wingRight,
    controls: state.controls,
    fuel: state.fuel,
  };
  for (const hit of hits) {
    health[hit.component] = clamp(health[hit.component] - Math.max(0, hit.amount), 0, 1);
  }
  return {
    ...state,
    ...health,
    wingsFailed: state.wingsFailed || health.wingLeft <= 0 || health.wingRight <= 0,
  };
}

/** Lift multiplier left after structural damage — a shed wing is not a gentle penalty. */
export function structuralLiftFactor(state: DamageState, tunables: Tunables): number {
  if (state.wingsFailed) return tunables.wingFailureLiftFactor;
  return clamp((state.wingLeft + state.wingRight) / 2, 0.5, 1);
}

/** Uncommanded roll from asymmetric wing damage, rad/s, positive to the right. */
export function damageRollBias(state: DamageState, tunables: Tunables): number {
  return (state.wingLeft - state.wingRight) * tunables.wingFailureRollRadPerSec;
}

/**
 * The nose-down weathervane of a wreck, rad/s (AD-014).
 *
 * "Wrecked" is the caller's judgement, and it is two cases: the wing structure
 * has gone, or the pilot is dead. Either way there is nobody and nothing holding
 * the nose up, which is the only property this models.
 *
 * Modelling wing failure as *lost lift alone* left the wreck in a deep stall: it
 * could not hold altitude, so it sank; sinking with the nose still level drove
 * the angle of attack towards ninety degrees; and at ninety degrees the drag of
 * a flat-plate aeroplane is enormous, so it settled into a stable ten-metre-a-
 * second parachute descent that took two minutes to come down from a dogfight.
 * That equilibrium is the whole reason kills used to end in mid-air.
 *
 * What breaks it is the thing an unflown aeroplane actually does: with nobody
 * holding the stick back, it weathervanes nose-first into its own airflow. So
 * the bias is proportional to the angle of attack and drives it towards zero.
 * As alpha falls the flat plate becomes a dart, drag collapses, and the wreck
 * accelerates into the ground the way a shot-down aeroplane is supposed to.
 *
 * Deliberately *not* a constant nose-down rate: a constant rate is a loop, not a
 * dive — it tumbles the wreck through inverted and back and averages out to the
 * same mush it was meant to cure. It is the alpha term that makes this stable.
 *
 * **AD-014b: nose-DOWN only, hence the zero ceiling.** A symmetric restoring
 * term would be a trim system rather than a weathervane, and it would fly the
 * wreck back up. A wreck that has nosed over does not stop at alpha = 0 — the
 * bias carries it a few degrees past, alpha goes *negative*, and an unclamped
 * `-alpha * gain` then commands nose-**up**, at up to the full 34 °/s of the
 * limit, sustained, until the dive is gone and the aeroplane is wallowing above
 * the ground. AD-014b §3b has the trace and the 99 s it costs.
 *
 * The ceiling at zero says the only thing this module's own sentence claims:
 * "there is nobody and nothing holding the nose up". A wreck's airframe may
 * weathervane the nose *down* into the airflow; nothing aboard it is entitled to
 * raise the nose. The dive is therefore kept, and the wreck arrives.
 */
export function wreckPitchBias(wrecked: boolean, tunables: Tunables, alphaRad: number): number {
  if (!wrecked) return 0;
  const limit = tunables.wingFailurePitchRadPerSec;
  // The law seeks a null, and which null it seeks is the whole of AD-014b. It
  // is the **fuselage** that weathervanes into the airflow, so the null is at
  // body α = 0. Left on the wing's angle it would null at α_body = −incidence,
  // i.e. hold a wreck's nose 5.5° *above* its own flight path for ever — the
  // "flies the wreck back up" failure this function's header exists to refuse.
  const bodyAlpha = bodyAlphaRad(alphaRad, tunables.wingIncidenceRad);
  return clamp(-bodyAlpha * tunables.wingFailurePitchGain, -limit, 0);
}
